/**
 * Patient Requirements — shared constants (GDN-3224, PRD Slice A).
 */

/**
 * Requirement kinds the backend accepts. Mirrors the `kind` pattern on
 * DefinitionCreateRequest (`^(payment_method|insurance_card|form)$`); the
 * generated type is a bare `string`, so this is the FE's narrowing.
 */
export const REQUIREMENT_KINDS = ['payment_method', 'insurance_card', 'form'] as const;

export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

/**
 * Kinds an org may hold at most one of. AC-1: "Staff cannot create a second
 * payment-method requirement for the same org." The backend enforces this with
 * a 409 (GDN-3222); the catalog withholds the option so staff never reach it.
 */
export const SINGLETON_KINDS: readonly RequirementKind[] = ['payment_method'];

/**
 * Kinds the catalog can create today. `form` requirements are Slice C
 * (AC-17 … AC-19, GDN-3229/GDN-3231) and are created from a form, not here —
 * offering the bare kind would produce a definition with nothing to submit.
 */
export const CREATABLE_KINDS: readonly RequirementKind[] = [
  'payment_method',
  'insurance_card',
];

/** Staff-facing names for each kind. Not the patient-facing label. */
export const REQUIREMENT_KIND_LABELS: Record<RequirementKind, string> = {
  payment_method: 'Payment method on file',
  insurance_card: 'Insurance card',
  form: 'Form',
};

export const isRequirementKind = (value: string): value is RequirementKind =>
  (REQUIREMENT_KINDS as readonly string[]).includes(value);

/**
 * Staff-facing name for a definition's kind, falling back to the raw value so a
 * kind added by the backend ahead of the frontend still renders something real
 * rather than an empty cell.
 */
export const requirementKindLabel = (kind: string): string =>
  isRequirementKind(kind) ? REQUIREMENT_KIND_LABELS[kind] : kind;

/** Patient-facing label bound, matched to the settings text fields around it. */
export const MAX_LABEL_LENGTH = 120;
